import {
  CapTableOrdering,
  CapTableOrderingMode,
  defaultStakeholderTypeOrder,
  WithinTypeSort,
} from '../../../types/snapshot.types';
import { StakeholderType } from '../../../types/stakeholder.types';

export const compareIssuedThenName = (
  aIssuedPercentage: number,
  bIssuedPercentage: number,
  aName: string,
  bName: string,
): number => {
  const issuedDiff = bIssuedPercentage - aIssuedPercentage;
  if (issuedDiff !== 0) return issuedDiff;

  return aName.localeCompare(bName, 'en', { sensitivity: 'base' });
};

export const sortByIssuedPercentageDesc = <T extends { issuedPercentage: number; name: string }>(
  items: T[],
): T[] =>
  [...items].sort((a, b) =>
    compareIssuedThenName(a.issuedPercentage, b.issuedPercentage, a.name, b.name),
  );

export const sortStakeholderTypesFoundersFirst = <
  T extends { issuedPercentage: number; type: StakeholderType },
>(
  types: T[],
  getTypeName: (type: StakeholderType) => string,
): T[] =>
  [...types].sort((a, b) => {
    const aIsFounder = a.type === StakeholderType.FOUNDER;
    const bIsFounder = b.type === StakeholderType.FOUNDER;
    if (aIsFounder !== bIsFounder) return aIsFounder ? -1 : 1;

    return compareIssuedThenName(
      a.issuedPercentage,
      b.issuedPercentage,
      getTypeName(a.type),
      getTypeName(b.type),
    );
  });

export const sortStakeholdersFoundersFirst = <
  T extends { issuedPercentage: number; name: string; type: StakeholderType },
>(
  stakeholders: T[],
): T[] =>
  [...stakeholders].sort((a, b) => {
    const aIsFounder = a.type === StakeholderType.FOUNDER;
    const bIsFounder = b.type === StakeholderType.FOUNDER;
    if (aIsFounder !== bIsFounder) return aIsFounder ? -1 : 1;

    return compareIssuedThenName(a.issuedPercentage, b.issuedPercentage, a.name, b.name);
  });

export const sortShareClassHoldersByIssuedPercentage = <
  T extends { name: string; shareClasses: { id: string; issuedPercentage: number }[] },
>(
  stakeholders: T[],
  shareClassId: string,
): T[] =>
  [...stakeholders].sort((a, b) => {
    const aIssuedPercentage =
      a.shareClasses.find((shareClass) => shareClass.id === shareClassId)?.issuedPercentage ?? 0;
    const bIssuedPercentage =
      b.shareClasses.find((shareClass) => shareClass.id === shareClassId)?.issuedPercentage ?? 0;

    return compareIssuedThenName(aIssuedPercentage, bIssuedPercentage, a.name, b.name);
  });

const issuedOfShareClass = <
  T extends { shareClasses: { diluted: number; id: string; issuedPercentage: number }[] },
>(
  stakeholder: T,
  shareClassId: string,
): number =>
  stakeholder.shareClasses.find((shareClass) => shareClass.id === shareClassId)?.issuedPercentage ??
  0;

export const groupStakeholdersByTypeSorted = <
  T extends { issuedPercentage: number; name: string; type: StakeholderType },
>(
  stakeholders: T[],
): Map<StakeholderType, T[]> => {
  const grouped = new Map<StakeholderType, T[]>();

  for (const stakeholder of stakeholders) {
    const bucket = grouped.get(stakeholder.type);
    if (bucket) bucket.push(stakeholder);
    else grouped.set(stakeholder.type, [stakeholder]);
  }

  for (const bucket of grouped.values()) {
    bucket.sort((a, b) =>
      compareIssuedThenName(a.issuedPercentage, b.issuedPercentage, a.name, b.name),
    );
  }

  return grouped;
};

export const groupShareClassHoldersSorted = <
  T extends {
    name: string;
    shareClasses: { diluted: number; id: string; issuedPercentage: number }[];
  },
>(
  stakeholders: T[],
): Map<string, T[]> => {
  const grouped = new Map<string, T[]>();

  for (const stakeholder of stakeholders) {
    for (const shareClass of stakeholder.shareClasses) {
      if (shareClass.diluted <= 0) continue;

      const bucket = grouped.get(shareClass.id);
      if (bucket) bucket.push(stakeholder);
      else grouped.set(shareClass.id, [stakeholder]);
    }
  }

  for (const [shareClassId, bucket] of grouped) {
    bucket.sort((a, b) =>
      compareIssuedThenName(
        issuedOfShareClass(a, shareClassId),
        issuedOfShareClass(b, shareClassId),
        a.name,
        b.name,
      ),
    );
  }

  return grouped;
};

const compareByName = (aName: string, bName: string): number =>
  aName.localeCompare(bName, 'en', { sensitivity: 'base' });

/**
 * The "Within each type, sort by" rule from the Ordering tab. Ownership ties fall back to the
 * name so that two holders of an equal stake keep a stable, readable order.
 */
export const compareWithinType = (
  withinType: WithinTypeSort,
  aIssuedPercentage: number,
  bIssuedPercentage: number,
  aName: string,
  bName: string,
): number => {
  switch (withinType) {
    case WithinTypeSort.ALPHABETICAL_ASC:
      return compareByName(aName, bName);
    case WithinTypeSort.ALPHABETICAL_DESC:
      return compareByName(bName, aName);
    case WithinTypeSort.OWNERSHIP_ASC: {
      const issuedDiff = aIssuedPercentage - bIssuedPercentage;
      return issuedDiff !== 0 ? issuedDiff : compareByName(aName, bName);
    }
    case WithinTypeSort.OWNERSHIP_DESC:
      return compareIssuedThenName(aIssuedPercentage, bIssuedPercentage, aName, bName);
  }
};

// A type the viewer's saved order does not mention — one added to the product after they arranged
// the list — sorts after every type it does mention rather than jumping to the front.
const typeRank = (typeOrder: StakeholderType[], type: StakeholderType): number => {
  const index = typeOrder.indexOf(type);

  return index === -1 ? typeOrder.length : index;
};

const isCustom = (ordering: CapTableOrdering): boolean =>
  ordering.mode === CapTableOrderingMode.CUSTOM;

/**
 * Repairs an ordering read back from storage: drops types that no longer exist, de-duplicates, and
 * appends types added to the product since it was saved, so a stale entry can never hide a type.
 */
export const normalizeCapTableOrdering = (
  ordering: null | Partial<CapTableOrdering> | undefined,
): CapTableOrdering => {
  const knownTypes = new Set<string>(Object.values(StakeholderType));
  const savedOrder = Array.isArray(ordering?.typeOrder) ? ordering.typeOrder : [];
  const typeOrder = [...new Set(savedOrder.filter((type) => knownTypes.has(type)))];

  for (const type of defaultStakeholderTypeOrder) {
    if (!typeOrder.includes(type)) typeOrder.push(type);
  }

  return {
    mode:
      ordering?.mode === CapTableOrderingMode.CUSTOM
        ? CapTableOrderingMode.CUSTOM
        : CapTableOrderingMode.DEFAULT,
    typeOrder,
    withinType:
      ordering?.withinType && Object.values(WithinTypeSort).includes(ordering.withinType)
        ? ordering.withinType
        : WithinTypeSort.OWNERSHIP_DESC,
  };
};

/**
 * Folds a drag made on the visible subset of types back into the full saved sequence: the Ordering
 * tab only lists the types the company holds, but the stored order covers every type so a company
 * that later gains one does not find it wherever the list happened to end.
 *
 * Types the tab did not show keep the slots they occupy; the shown ones fill their own slots in
 * their new sequence. `nextVisibleOrder` must be a permutation of the visible members of
 * `fullOrder`, which is what a drag on that list always produces.
 */
export const applyVisibleTypeOrder = (
  fullOrder: StakeholderType[],
  nextVisibleOrder: StakeholderType[],
): StakeholderType[] => {
  const isVisible = new Set(nextVisibleOrder);
  const remaining = [...nextVisibleOrder];

  return fullOrder.map((type) => (isVisible.has(type) ? (remaining.shift() ?? type) : type));
};

export const sortStakeholdersByOrdering = <
  T extends { issuedPercentage: number; name: string; type: StakeholderType },
>(
  stakeholders: T[],
  ordering: CapTableOrdering,
): T[] => {
  if (!isCustom(ordering)) return sortStakeholdersFoundersFirst(stakeholders);

  return [...stakeholders].sort((a, b) => {
    const rankDiff = typeRank(ordering.typeOrder, a.type) - typeRank(ordering.typeOrder, b.type);
    if (rankDiff !== 0) return rankDiff;

    return compareWithinType(
      ordering.withinType,
      a.issuedPercentage,
      b.issuedPercentage,
      a.name,
      b.name,
    );
  });
};

export const sortStakeholderTypesByOrdering = <
  T extends { issuedPercentage: number; type: StakeholderType },
>(
  types: T[],
  ordering: CapTableOrdering,
  getTypeName: (type: StakeholderType) => string,
): T[] => {
  if (!isCustom(ordering)) return sortStakeholderTypesFoundersFirst(types, getTypeName);

  return [...types].sort((a, b) => {
    const rankDiff = typeRank(ordering.typeOrder, a.type) - typeRank(ordering.typeOrder, b.type);
    if (rankDiff !== 0) return rankDiff;

    return compareWithinType(
      ordering.withinType,
      a.issuedPercentage,
      b.issuedPercentage,
      getTypeName(a.type),
      getTypeName(b.type),
    );
  });
};

/**
 * By Share Class keeps share class as its primary grouping; inside one class the holders are laid
 * out by the custom type sequence first, then by the within-type rule applied to the stake held in
 * *that* class — the same number the column shows.
 */
export const sortShareClassHoldersByOrdering = <
  T extends {
    name: string;
    shareClasses: { diluted: number; id: string; issuedPercentage: number }[];
    type: StakeholderType;
  },
>(
  stakeholders: T[],
  shareClassId: string,
  ordering: CapTableOrdering,
): T[] => {
  if (!isCustom(ordering))
    return sortShareClassHoldersByIssuedPercentage(stakeholders, shareClassId);

  return [...stakeholders].sort((a, b) => {
    const rankDiff = typeRank(ordering.typeOrder, a.type) - typeRank(ordering.typeOrder, b.type);
    if (rankDiff !== 0) return rankDiff;

    return compareWithinType(
      ordering.withinType,
      issuedOfShareClass(a, shareClassId),
      issuedOfShareClass(b, shareClassId),
      a.name,
      b.name,
    );
  });
};

export const groupStakeholdersByTypeOrdered = <
  T extends { issuedPercentage: number; name: string; type: StakeholderType },
>(
  stakeholders: T[],
  ordering: CapTableOrdering,
): Map<StakeholderType, T[]> => {
  if (!isCustom(ordering)) return groupStakeholdersByTypeSorted(stakeholders);

  const grouped = new Map<StakeholderType, T[]>();

  for (const stakeholder of sortStakeholdersByOrdering(stakeholders, ordering)) {
    const bucket = grouped.get(stakeholder.type);
    if (bucket) bucket.push(stakeholder);
    else grouped.set(stakeholder.type, [stakeholder]);
  }

  return grouped;
};

export const groupShareClassHoldersByOrdering = <
  T extends {
    name: string;
    shareClasses: { diluted: number; id: string; issuedPercentage: number }[];
    type: StakeholderType;
  },
>(
  stakeholders: T[],
  ordering: CapTableOrdering,
): Map<string, T[]> => {
  if (!isCustom(ordering)) return groupShareClassHoldersSorted(stakeholders);

  const grouped = new Map<string, T[]>();

  for (const stakeholder of stakeholders) {
    for (const shareClass of stakeholder.shareClasses) {
      if (shareClass.diluted <= 0) continue;

      const bucket = grouped.get(shareClass.id);
      if (bucket) bucket.push(stakeholder);
      else grouped.set(shareClass.id, [stakeholder]);
    }
  }

  for (const [shareClassId, bucket] of grouped) {
    bucket.sort((a, b) => {
      const rankDiff = typeRank(ordering.typeOrder, a.type) - typeRank(ordering.typeOrder, b.type);
      if (rankDiff !== 0) return rankDiff;

      return compareWithinType(
        ordering.withinType,
        issuedOfShareClass(a, shareClassId),
        issuedOfShareClass(b, shareClassId),
        a.name,
        b.name,
      );
    });
  }

  return grouped;
};
